import Button from "components/foundation/Button";
import Divider from "components/foundation/Divider";
import React, { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import styled from "styled-components";
import { UserProfileInput } from "../UserProfileInput/UserProfileInput";
import UserProfilePhoneInput from "../UserProfileInput/UserProfilePhoneInput";
import UserProfileSignInput from "../UserProfileInput/UserProfileSignInput";
import { ProfileFormData } from "./UserProfileFormCard";
import REGEX from "config/regex";
import { UserDetailResponse } from "apis/data-contracts";

interface UserProfileMobileFormProps {
  defaultData: UserDetailResponse;
  currentPassword: string;
}

const UserProfileMobileForm = ({
  defaultData,
  currentPassword,
}: UserProfileMobileFormProps) => {
  const {
    control,
    register,
    formState: { errors, isValid },
  } = useFormContext<ProfileFormData>();

  const [hasPhoneNumberError, setHasPhoneNumberError] = useState(false);

  return (
    <UserProfileMobileFormLayout>
      <h1>회원 가입 정보</h1>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "2.4rem",
        }}
      >
        <UserProfileMobileListItem label="회사">
          {defaultData.company}
        </UserProfileMobileListItem>
        <UserProfileMobileListItem label="부서">
          {defaultData.department}
        </UserProfileMobileListItem>
        <UserProfileMobileListItem label="직급">
          {defaultData.position}
        </UserProfileMobileListItem>
        <UserProfileMobileListItem label="이름">
          {defaultData.name}
        </UserProfileMobileListItem>
        <UserProfileSignInput
          label="서명이미지"
          src={defaultData.signImageUrl}
        />
        <UserProfileMobileListItem label="이메일">
          {defaultData.email}
        </UserProfileMobileListItem>
      </div>
      <Divider />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "2.8rem",
        }}
      >
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <UserProfilePhoneInput
              label="연락처"
              value={field.value}
              onPhoneChange={(value) => field.onChange(value)}
              onHasError={(value) => setHasPhoneNumberError(value)}
            />
          )}
        ></Controller>
        <UserProfileInput
          type="password"
          placeholder="비밀번호를 지정해 주세요"
          label="변경할 비밀번호"
          {...register("password", {
            pattern: {
              value: REGEX.PASSWORD,
              message: "영문, 숫자, 특수문자를 포함하여 8자 이상 20자 이내",
            },
            validate: (value) =>
              value !== currentPassword ||
              "이전에 사용한 비밀번호는 사용할 수 없습니다.",
          })}
          errorMessage={errors.password?.message}
        >
          {!errors.password && (
            <UserProfileMobileDescription>
              영문, 숫자, 특수문자를 포함하여 8자이상 20자이내
            </UserProfileMobileDescription>
          )}
        </UserProfileInput>
        <UserProfileInput
          type="password"
          placeholder="비밀번호를 다시 한 번 입력해 주세요"
          label="비밀번호 확인"
          {...register("passwordConfirm", {
            pattern: {
              value: REGEX.PASSWORD,
              message: "영문, 숫자, 특수문자를 포함하여 8자 이상 20자 이내",
            },
          })}
        />
      </div>
      <Button
        size="large"
        type="submit"
        disabled={hasPhoneNumberError || !isValid}
      >
        저장
      </Button>
    </UserProfileMobileFormLayout>
  );
};

export default UserProfileMobileForm;

export const UserProfileMobileDescription = styled.span`
  padding: 0.8rem 0rem 0rem 1.4rem;
  ${({ theme }) => theme.typography.caption1.regular};
  color: ${({ theme }) => theme.color.text.highEmphasis};
`;

const UserProfileMobileFormLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.4rem;
  padding: 1.6rem;

  h1 {
    ${({ theme }) => theme.typography.t1.semibold};
    margin-bottom: 1rem;
  }
`;

interface UserProfileMobileListItemProps {
  label: string;
  children: React.ReactNode;
}

const UserProfileMobileListItem = ({
  label,
  children,
}: UserProfileMobileListItemProps) => {
  return (
    <UserProfileMobileListItemLayout>
      <h2>{label}</h2>
      {children}
    </UserProfileMobileListItemLayout>
  );
};

const UserProfileMobileListItemLayout = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  ${({ theme }) => theme.typography.body3.regular};

  h2 {
    min-width: 7rem;
    ${({ theme }) => theme.typography.body3.medium};
  }
`;
